import { has } from 'lodash'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { Model } from '../../types'
import { OnboardingStatus, RbacRole } from '../../constants'
import type { AuthenticationState } from '../types/state'

const initialState: AuthenticationState = {
  token: null,
  currentUser: null
}

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    setToken(state, { payload }: PayloadAction<Model.Token>) {
      state.token = payload
    },
    setCurrentUserAction(state, { payload }: PayloadAction<Model.CurrentUser>) {
      state.currentUser = payload
    },
    setMfaStatusAction(state, { payload }: PayloadAction<boolean>) {
      if (state.currentUser) {
        state.currentUser.mfa.enabled = payload
      }
    }
  },
  selectors: {
    hasTokenSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) => !!state.token
    ),
    getTokenSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) => state.token
    ),
    getAccessTokenSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) => state.token?.accessToken
    ),
    getCurrentUserSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) => state.currentUser
    ),
    hasCurrentUserCompletedOnboardingSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) =>
        state.currentUser?.onboarding.status === OnboardingStatus.Completed
    ),
    hasCurrentUserIncompleteOnboardingSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) =>
        state.currentUser?.onboarding.status === OnboardingStatus.Pending
    ),
    selectOnboardingSelector: createSelector(
      (state: AuthenticationState) => state.currentUser?.onboarding,
      (onboarding) => onboarding?.steps
    ),
    isInitialPasswordChangeRequiredSelector: createSelector(
      (state: AuthenticationState): Model.CurrentUser['onboarding']['steps'] | undefined =>
        authenticationSlice.getSelectors().selectOnboardingSelector(state),
      (state) =>
        state && has(state, 'initialPasswordChanged') && state.initialPasswordChanged === false
    ),
    isEnableMFARequiredSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) => {
        return (
          has(state.currentUser?.onboarding.steps, 'mfaEnabled'),
          state.currentUser?.onboarding.steps.mfaEnabled === false
        )
      }
    ),
    isCurrentUserAdminSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) =>
        !!state.currentUser &&
        state.currentUser.roleAssignments?.some((a) => a.role.name === RbacRole.GlobalAdmin)
    ),
    isCurrentUserAdminOrGlobalAdminSelector: createSelector(
      (state: AuthenticationState) => state,
      (state: AuthenticationState) =>
        !!state.currentUser &&
        state.currentUser.roleAssignments?.some(
          (assignment) =>
            assignment.role.name === RbacRole.Admin || assignment.role.name === RbacRole.GlobalAdmin
        )
    )
  }
})

export const {
  hasTokenSelector,
  getTokenSelector,
  getAccessTokenSelector,
  getCurrentUserSelector,
  hasCurrentUserCompletedOnboardingSelector,
  hasCurrentUserIncompleteOnboardingSelector,
  isInitialPasswordChangeRequiredSelector,
  isEnableMFARequiredSelector,
  isCurrentUserAdminSelector,
  isCurrentUserAdminOrGlobalAdminSelector
} = authenticationSlice.selectors

export const { setToken, setCurrentUserAction, setMfaStatusAction } = authenticationSlice.actions

export default authenticationSlice
